import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useMutationHTTPRequest } from 'api/hooks/useHTTPRequest';
import { logError } from 'lib/sentry/logError';

import { JournalNote, useJournalQueryActions } from 'features/journal';

import { showErrorToast } from 'shared/components/Toast';

export const useDeleteJournalNoteReaction = ({
  participantId,
}: {
  participantId: string;
}) => {
  const { request } = useMutationHTTPRequest();

  const {
    getJournalRelevantQueries,
    cancelJournalRelevantQueries,
    cancelJournalNoteQueries,
    getPreviousJournalNoteValue,
    updateJournalNoteQueryData,
    updateJournalRelevantQueries,
    invalidateJournalNoteQueries,
  } = useJournalQueryActions({ participantId });
  const queryClient = useQueryClient();

  return useMutation(
    async ({ reactionId, noteId }: MutationFnPropType) =>
      await request({
        url: `/api/journal/notes/${noteId}/reactions/${reactionId}/delete`,
      }),
    {
      onMutate: ({ reactionId, noteId }: MutationFnPropType) => {
        const relevantQueries = getJournalRelevantQueries();
        cancelJournalRelevantQueries();

        const previousJournalNotesData = relevantQueries.map(([key, data]) => ({
          key,
          data,
        }));

        updateJournalRelevantQueries((oldData) => {
          for (const page of oldData.pages) {
            for (const note of page.notes) {
              if (note.noteId === noteId) {
                const reactionIndex = note.reactions.findIndex(
                  (reaction: {
                    reactionId: number;
                    temporaryReactionId: number;
                  }) =>
                    reaction.reactionId === reactionId ||
                    reaction.temporaryReactionId === reactionId
                );
                if (reactionIndex !== -1) {
                  note.reactions.splice(reactionIndex, 1);
                }
              }
            }
          }
        });

        cancelJournalNoteQueries(noteId);

        const previousJournalNoteData = getPreviousJournalNoteValue(noteId);

        updateJournalNoteQueryData(noteId, (old: { note: JournalNote }) => {
          const reactions = old.note.reactions;
          const reactionIndex = reactions.findIndex(
            (reaction) =>
              reaction.reactionId === reactionId ||
              reaction.temporaryReactionId === reactionId
          );
          if (reactionIndex !== -1) {
            reactions.splice(reactionIndex, 1);
          }
        });

        return { previousJournalNotesData, previousJournalNoteData };
      },
      onSuccess: (_: JournalNote, { noteId }) => {
        invalidateJournalNoteQueries(noteId);
      },
      onError: (err, { noteId }, context) => {
        showErrorToast(
          "There's an error with deleting reaction. Please try again later."
        );

        context?.previousJournalNotesData.forEach(({ key, data }: any) => {
          queryClient.setQueryData(key, data);
        });

        updateJournalNoteQueryData(
          noteId,
          () => context?.previousJournalNoteData
        );

        logError(err);
      },
      onSettled: (_, __, { noteId }) => {
        invalidateJournalNoteQueries(noteId);
      },
    }
  );
};

type MutationFnPropType = {
  reactionId: number;
  noteId: string;
};
